import * as Sentry from '@sentry/react';
import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import App from './App';
import { ChunkLoadErrorBoundary } from './components/ChunkLoadErrorBoundary';
import { isChunkLoadError, reloadOnceForStaleChunk } from './utils/chunkReload';

const SENTRY_NOISE_PATTERNS = [
  "Unexpected token '<'",
  'Loading chunk',
  'Loading CSS chunk',
  'ChunkLoadError',
  'Chunk reload timed out',
  'Object Not Found Matching Id',
  'Failed to load Stripe.js',
  'Your card was declined',
  'card was declined',
  'We are unable to authenticate your payment method',
  'We are experiencing issues connecting to our payments provider',
];

function isSentryNoise(message: string): boolean {
  return SENTRY_NOISE_PATTERNS.some((pattern) => message.includes(pattern));
}

Sentry.init({
  dsn: process.env.REACT_APP_SENTRY_DSN,
  environment: process.env.REACT_APP_ENV ?? 'production',
  tracesSampleRate: 0.2,
  replaysOnErrorSampleRate: 1.0,
  replaysSessionSampleRate: 0.05,
  integrations: [
    Sentry.replayIntegration(),
    Sentry.browserTracingIntegration(),
  ],
  enabled: !!process.env.REACT_APP_SENTRY_DSN,
  ignoreErrors: SENTRY_NOISE_PATTERNS,
  beforeSend(event) {
    const message = event.exception?.values?.[0]?.value ?? event.message ?? '';
    if (isSentryNoise(message)) {
      return null;
    }
    return event;
  },
});

window.addEventListener('error', (event) => {
  const err = event.error;
  if (err instanceof Error && isChunkLoadError(err)) {
    reloadOnceForStaleChunk();
  }
});

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
);

root.render(
  <React.StrictMode>
    <ChunkLoadErrorBoundary>
      <App />
    </ChunkLoadErrorBoundary>
  </React.StrictMode>
);
